import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LMSDataService } from "@/services/lms_data";
import { ApprenticeshipAppointmentType } from "@/types/apprenticeship/enterprise";
import { EventSlot } from "@/types/lms/booking";
import { Copy, ExternalLink, Video } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export function AppointmentLinkDialog({ appointment, size = 20, children }: { appointment: ApprenticeshipAppointmentType; size?: number; children?: React.ReactNode }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [slot, setSlot] = useState<EventSlot | null>(null);

    const host_url = (event_slot: EventSlot): string => {
        if (event_slot.event_type.platform === "jitsi_meet") {
            return `https://meet.walter-learning.com/${event_slot.global_id}/${event_slot.jitsi_room_name}?jwt=${event_slot.host_jitsi_jwt}#config.prejoinConfig.enabled=true&config.notifications=[]&config.toolbarButtons=["microphone","camera","desktop","chat","participants-pane","tileview","toggle-camera","hangup","profile","fullscreen","noisesuppression","whiteboard","select-background","settings","shortcuts","embedmeeting","help","filmstrip","recording"]`;
        }
        if (event_slot.event_type.platform === "virtual_room") {
            return event_slot.host_virtual_room_url || "";
        }
        if (event_slot.event_type.platform === "google_meet") {
            return event_slot.host_google_meet_space_url || "";
        }
        if (event_slot.event_type.platform === "digital_samba") {
            return event_slot.host_digital_samba_room_url || "";
        }
        return "";
    };

    const participant_url = (event_slot: EventSlot, participant: { jitsi_jwt: string | null; virtual_room_url: string | null; google_meet_space_url: string | null; digital_samba_room_url: string | null }): string => {
        if (event_slot.event_type.platform === "jitsi_meet") {
            return `https://meet.walter-learning.com/${event_slot.global_id}/${event_slot.jitsi_room_name}?jwt=${participant.jitsi_jwt}#config.prejoinConfig.enabled=true&config.notifications=[]&config.toolbarButtons=["microphone","camera","desktop","chat","participants-pane","tileview","toggle-camera","hangup","profile","fullscreen","noisesuppression","whiteboard","select-background","settings","shortcuts","embedmeeting","help","filmstrip","recording"]`;
        }
        if (event_slot.event_type.platform === "virtual_room") {
            return participant.virtual_room_url || "";
        }
        if (event_slot.event_type.platform === "google_meet") {
            return participant.google_meet_space_url || "";
        }
        if (event_slot.event_type.platform === "digital_samba") {
            return participant.digital_samba_room_url || "";
        }
        return "";
    };

    const copyLink = (url: string) => {
        if (!url) {
            toast.error("Lien indisponible");
            return;
        }
        navigator.clipboard
            .writeText(url)
            .then(() => toast.success("Lien copié dans le presse-papier"))
            .catch(() => toast.error("Erreur lors de la copie du lien"));
    };

    // Opens the LMS access link: redirects in-app to /evenement for Digital Samba
    // (embedded room), or to the provider for other platforms. No re-implementation
    // of the player in the CRM.
    const joinLink = (url: string | null) => {
        if (!url) {
            toast.error("Lien indisponible");
            return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
    };

    useEffect(() => {
        if (open) {
            const event_slot_id = appointment.external_id.split(":")[1];
            setLoading(true);
            LMSDataService.client
                .url(`/booking/crm/event_slots/${event_slot_id}/`)
                .get()
                .json()
                .then(data => {
                    setSlot(data as EventSlot);
                })
                .catch(error => {
                    console.error("Error:", error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children || <ExternalLink width={size} height={size} cursor="pointer" />}</DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Lien pour accéder au rendez-vous</DialogTitle>
                    {loading && <DialogDescription>Chargement...</DialogDescription>}
                    {!loading && !slot && <DialogDescription>Impossible de récupérer les informations du rendez-vous</DialogDescription>}
                </DialogHeader>

                {slot && (
                    <div className="space-y-2">
                        {slot.participants
                            .filter(p => ["confirmed", "to_be_confirmed"].includes(p.status))
                            .map((p, index) => {
                                // Prefer the LMS access link (redirects in-app to /evenement for
                                // Digital Samba), fall back to the directly-built provider URL.
                                // Join and copy share the same link to stay consistent.
                                const link = p.access_url || participant_url(slot, p);
                                return (
                                    <div key={index} className="flex items-center justify-between gap-2">
                                        <span className="truncate text-sm">
                                            {p.user ? p.user.last_name : p.last_name} {p.user ? p.user.first_name : p.first_name}
                                        </span>
                                        <div className="flex shrink-0 items-center gap-3">
                                            <button type="button" className="flex items-center gap-1 text-blue-600 hover:underline" onClick={() => joinLink(link)}>
                                                <Video size={16} /> Rejoindre
                                            </button>
                                            <button type="button" className="flex items-center gap-1 text-gray-600 hover:underline" onClick={() => copyLink(link)}>
                                                <Copy size={16} /> Copier
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        {(() => {
                            const hostLink = slot.host_access_url || host_url(slot);
                            return (
                                <div className="flex items-center justify-between gap-2 border-t pt-2">
                                    <span className="truncate text-sm font-medium">Intervenant</span>
                                    <div className="flex shrink-0 items-center gap-3">
                                        <button type="button" className="flex items-center gap-1 text-blue-600 hover:underline" onClick={() => joinLink(hostLink)}>
                                            <Video size={16} /> Rejoindre
                                        </button>
                                        <button type="button" className="flex items-center gap-1 text-gray-600 hover:underline" onClick={() => copyLink(hostLink)}>
                                            <Copy size={16} /> Copier
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
